import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    forwardRef,
    inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseHorizontalNavigationBasicItemComponent } from '@fuse/components/navigation/horizontal/components/basic/basic.component';
import { FuseHorizontalNavigationDividerItemComponent } from '@fuse/components/navigation/horizontal/components/divider/divider.component';
import { FuseHorizontalNavigationComponent } from '@fuse/components/navigation/horizontal/horizontal.component';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'fuse-horizontal-navigation-branch-item',
    templateUrl: './branch.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgClass,
        MatMenuModule,
        NgTemplateOutlet,
        FuseHorizontalNavigationBasicItemComponent,
        forwardRef(() => FuseHorizontalNavigationBranchItemComponent),
        FuseHorizontalNavigationDividerItemComponent,
        MatTooltipModule,
        MatIconModule
    ]
})
export class FuseHorizontalNavigationBranchItemComponent implements OnInit, OnDestroy {
    static ngAcceptInputType_child: BooleanInput;
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _fuseNavigationService = inject(FuseNavigationService);

    @Input() child: boolean = false;
    @Input() item: FuseNavigationItem;
    @Input() name: string;
    @ViewChild('matMenu', { static: true }) matMenu: MatMenu;
    @ViewChild('matMenuTrigger') matMenuTrigger: MatMenuTrigger;

    private _fuseHorizontalNavigationComponent: FuseHorizontalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _mouseX = 0;
    private _mouseY = 0;

    ngOnInit(): void {
        this._fuseHorizontalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        this._fuseHorizontalNavigationComponent.onRefreshed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });

        document.addEventListener('mousemove', this.trackMousePosition.bind(this));
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        document.removeEventListener('mousemove', this.trackMousePosition.bind(this));
    }

    trackMousePosition(event: MouseEvent): void {
        this._mouseX = event.clientX;
        this._mouseY = event.clientY;
    }

    triggerChangeDetection(): void {
        this._changeDetectorRef.markForCheck();
    }


    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    openMenu(): void {
        if (this.matMenuTrigger && !this.matMenuTrigger.menuOpen) {
            this.matMenuTrigger.openMenu();
            this.triggerChangeDetection();
        }
    }

    closeMenu(): void {
        setTimeout(() => {
            if (this.matMenuTrigger && this.matMenuTrigger.menuOpen && !this.isMouseInMenu()) {
                this.matMenuTrigger.closeMenu();
                this.triggerChangeDetection();
            }
        }, 100);
    }

    private isMouseInMenu(): boolean {
        if (!this.matMenuTrigger?.menu) {
            return false;
        }

        const menuElement = document.getElementById(this.matMenuTrigger.menu.panelId);
        if (!menuElement) {
            return false;
        }

        const rect = menuElement.getBoundingClientRect();

        return (
            this._mouseX >= rect.left &&
            this._mouseX <= rect.right &&
            this._mouseY >= rect.top &&
            this._mouseY <= rect.bottom
        );
    }
}
